<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Download, X } from "lucide-vue-next";
import type { V2Asset } from "@/types";
import { apiBlob } from "@/services/api";

const props = defineProps<{ asset: V2Asset | null }>();
const emit = defineEmits<{ close: [] }>();
const { t } = useI18n();
const objectUrl = ref("");
const loading = ref(false);
const error = ref("");
const kind = computed(() => {
  const mime = props.asset?.mimeType || "";
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("video/")) return "video";
  return "other";
});

function revoke() {
  if (objectUrl.value) URL.revokeObjectURL(objectUrl.value);
  objectUrl.value = "";
}
watch(
  () => props.asset,
  async (asset) => {
    revoke();
    error.value = "";
    if (!asset) return;
    loading.value = true;
    try {
      objectUrl.value = URL.createObjectURL(await apiBlob(asset.previewUrl.slice(4)));
    } catch (reason) {
      error.value = reason instanceof Error ? reason.message : t("asset.previewFailed");
    } finally {
      loading.value = false;
    }
  },
  { immediate: true },
);
async function download() {
  if (!props.asset) return;
  const url = URL.createObjectURL(await apiBlob(props.asset.downloadUrl.slice(4)));
  const link = document.createElement("a");
  link.href = url;
  link.download = props.asset.originalName;
  link.click();
  URL.revokeObjectURL(url);
}
onUnmounted(revoke);
</script>

<template>
  <Teleport to="body">
    <div v-if="asset" class="preview-backdrop" @click.self="emit('close')">
      <section class="preview-modal">
        <header><div><b>{{ asset.originalName }}</b><small>{{ asset.mimeType }}</small></div><div class="actions"><button class="icon-btn" :title="t('asset.downloadOriginal')" @click="download"><Download :size="18" /></button><button class="icon-btn" :title="t('asset.closePreview')" @click="emit('close')"><X :size="20" /></button></div></header>
        <main>
          <p v-if="loading" class="muted">{{ t("asset.loading") }}</p>
          <p v-else-if="error" class="error">{{ error }}</p>
          <img v-else-if="kind === 'image'" :src="objectUrl" :alt="asset.originalName" />
          <iframe v-else-if="kind === 'pdf'" :src="objectUrl" :title="asset.originalName"></iframe>
          <video v-else-if="kind === 'video'" :src="objectUrl" controls></video>
          <div v-else class="unsupported"><p>{{ t("asset.unsupported") }}</p><button class="btn btn--primary" @click="download"><Download :size="17" />{{ t("asset.download") }}</button></div>
        </main>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.preview-backdrop{position:fixed;inset:0;z-index:60;display:grid;place-items:center;padding:24px;background:rgba(17,25,32,.62)}.preview-modal{display:grid;grid-template-rows:auto minmax(0,1fr);width:min(980px,100%);height:min(760px,92vh);background:#fff}.preview-modal header{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:13px 16px;border-bottom:1px solid var(--line)}.preview-modal header b,.preview-modal header small{display:block}.preview-modal header small{color:var(--muted)}.actions{display:flex;gap:6px}.icon-btn{display:grid;width:38px;height:38px;place-items:center;border:1px solid var(--line);background:#fff}.preview-modal main{display:grid;min-height:0;place-items:center;overflow:auto;background:#f2f4f4}.preview-modal img,.preview-modal video{max-width:100%;max-height:100%;object-fit:contain}.preview-modal iframe{width:100%;height:100%;border:0}.unsupported{text-align:center}.unsupported .btn{display:inline-flex;align-items:center;gap:7px}.error{color:var(--danger)}@media(max-width:700px){.preview-backdrop{padding:0}.preview-modal{width:100%;height:100vh}.preview-modal header{padding:10px 12px}}
</style>
